import { useState, useCallback, useRef, useEffect } from 'react';
import { AccommodationInfo, AccommodationGroup, Quotation } from '@/components/quotation/types';

// Extended interface for properties not in the base AccommodationInfo
export interface ExtendedAccommodationInfo extends AccommodationInfo {
  checkIn?: string;
  checkInDate?: string;
  checkInTime?: string;
  checkOut?: string;
  checkOutDate?: string;
  checkOutTime?: string;
  amenities?: string[];
  website?: string;
}

export interface AccommodationsByPage {
  [groupId: string]: {
    pageTitle: string;
    pageDescription?: string;
    accommodations: ExtendedAccommodationInfo[];
  };
}

export function useAccommodationsState(quotation: Quotation) {
  // Group accommodations based on grouping mode
  const accommodationsByPage: AccommodationsByPage = {};
  
  // Helper to find accommodation across all pages
  const findAccommodationById = (id: string): ExtendedAccommodationInfo | undefined => {
    for (const page of quotation.pages) {
      const found = page.accommodations?.find(acc => acc.id === id);
      if (found) {
        return found as ExtendedAccommodationInfo;
      }
    }
    return undefined;
  };
  
  // Check if we should use custom groups
  const useCustomGroups = quotation.useCustomAccommodationGroups && quotation.accommodationGroups && quotation.accommodationGroups.length > 0;
  
  if (useCustomGroups) {
    // Sort groups by order
    const sortedGroups = [...(quotation.accommodationGroups || [])].sort((a, b) => a.order - b.order);
    
    // Create accommodations grouping based on custom groups
    sortedGroups.forEach(group => {
      const groupAccommodations: ExtendedAccommodationInfo[] = [];
      
      // Get accommodations for this group
      group.accommodationIds.forEach(accId => {
        const accommodation = findAccommodationById(accId);
        if (accommodation) {
          groupAccommodations.push(accommodation);
        }
      });
      
      if (groupAccommodations.length > 0) {
        accommodationsByPage[group.id] = {
          pageTitle: group.name,
          pageDescription: group.description,
          accommodations: groupAccommodations
        };
      }
    });
  } else {
    // Default grouping by page
    quotation.pages.forEach((page, pageIndex) => {
      if (page.accommodations && page.accommodations.length > 0) {
        accommodationsByPage[page.id] = {
          pageTitle: page.title || `Página ${pageIndex + 1}`,
          accommodations: page.accommodations as ExtendedAccommodationInfo[]
        };
      }
    });
  }
  
  // Check if there are any accommodations
  const hasAccommodations = Object.keys(accommodationsByPage).length > 0;
  
  // State to track active image for each accommodation
  const [activeImageIndices, setActiveImageIndices] = useState<Record<string, number>>({});
  
  // State to track collapsed page sections - initializing with true for all
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>(() => {
    // Create an object with true (collapsed) for all pageIds
    const initialState: Record<string, boolean> = {};
    Object.keys(accommodationsByPage).forEach(groupId => {
      initialState[groupId] = true;
    });
    return initialState;
  });
  
  // State to track expanded accommodation details
  const [expandedDetails, setExpandedDetails] = useState<Record<string, boolean>>({});

  // State to track hovered accommodation for map marker highlighting
  const [hoveredAccommodation, setHoveredAccommodation] = useState<string | null>(null);
  
  // Refs para o debounce
  const debounceTimeoutRef = useRef<number | null>(null);
  const lastHoveredIdRef = useRef<string | null>(null);

  // Toggle collapsed state for a section
  const toggleSection = useCallback((groupId: string) => {
    setCollapsedSections(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  }, []);
  
  // Toggle expanded details for an accommodation
  const toggleDetails = useCallback((accommodationId: string) => {
    setExpandedDetails(prev => ({
      ...prev,
      [accommodationId]: !prev[accommodationId]
    }));
  }, []);

  // Handle accommodation hover with debounce
  const handleAccommodationHover = useCallback((id: string | null) => {
    // Se é o mesmo ID que já está hovering, não faz nada
    if (id === lastHoveredIdRef.current) {
      return;
    }
    
    // Guarda o novo ID
    lastHoveredIdRef.current = id;
    
    // Limpa o timeout anterior se existir
    if (debounceTimeoutRef.current !== null) {
      window.clearTimeout(debounceTimeoutRef.current);
    }
    
    // Configura um novo timeout (100ms de debounce)
    debounceTimeoutRef.current = window.setTimeout(() => {
      setHoveredAccommodation(id);
      debounceTimeoutRef.current = null;
    }, 100);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current !== null) {
        window.clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Set active image index for a specific accommodation
  const setAccommodationActiveImageIndex = useCallback((accommodationId: string, index: number) => {
    setActiveImageIndices(prev => ({
      ...prev,
      [accommodationId]: index
    }));
  }, []);

  // Navigate image carousel
  const navigateImage = useCallback((accommodationId: string, direction: 'prev' | 'next', totalImages: number) => {
    setActiveImageIndices(prev => {
      const currentIndex = prev[accommodationId] || 0;
      let newIndex;
      
      if (direction === 'prev') {
        newIndex = currentIndex === 0 ? totalImages - 1 : currentIndex - 1;
      } else {
        newIndex = (currentIndex + 1) % totalImages;
      }
      
      return {
        ...prev,
        [accommodationId]: newIndex
      };
    });
  }, []);

  return {
    accommodationsByPage,
    hasAccommodations,
    activeImageIndices,
    collapsedSections,
    expandedDetails,
    hoveredAccommodation,
    toggleSection,
    toggleDetails,
    handleAccommodationHover,
    setAccommodationActiveImageIndex,
    navigateImage,
    isUsingCustomGroups: useCustomGroups
  };
} 